// The editor's commands (SuiteEditor's ProseCommands and the text views' key handling): emphasis, spans and the
// writer's colours, alignment, lists, block styles, scene breaks, footnotes, Return, Backspace and Delete. Each is a
// CodeMirror `StateCommand`, so it runs on a view (`editor.run(command)`, a keymap) or headless on any `{ state,
// dispatch }`. Every edit goes through the same filter as typing: the commands' own edits are marked exact (as the
// Swift's `replaceRun` edits are), everything else is decided and balanced there.
import { EditorSelection, Transaction, type EditorState, type StateCommand, type TransactionSpec } from '@codemirror/state';
import type { Alignment, InkColor, ListStyle } from '@writing-suite/core';
import { isNumbered, marker as listMarkerText, MAX_VERSE_INDENT, parse as parseProse, verseFence } from '@writing-suite/core';
import {
  aligned, alignment as alignmentOf, block, blockStyle as blockStyleOf, leadLength, listed, listStyle as listStyleOf,
  isInsideFence, isSceneBreakOrNote, nextFootnoteNumber, restyle, word, type BlockStyle,
} from './blocks.ts';
import { linkAt, parseListMarker, scan, stateAt, trimmed, type InlineState } from './markdown.ts';
import {
  clusterAfter, clusterBefore, inserting, keyDeleteRange, lineBreak, linkAddressFromTyped, linked, restyled, toggledEmphasis, verseLine,
  verseOpenerEnd, type Fix,
} from './rules.ts';
import { screenplayNewline, screenplayTab, setScreenplayElement } from './screenplay.ts';
import { bodyWindow, EditBuilder, windowAround } from './session.ts';
import {
  Exact, ExactCaret, layoutOf, pendingEmphasisField, setPendingEmphasis, toggledPending,
} from './state.ts';

type Dispatch = (tr: Transaction) => void;

const exact = (userEvent: string) => [Exact.of(true), ExactCaret.of(true), Transaction.userEvent.of(userEvent)];

function run(state: EditorState, dispatch: Dispatch, spec: TransactionSpec) { dispatch(state.update(spec)); }

/** Makes a balanced edit and puts the caret exactly where it belongs (`ScriveningsTextView.apply`). An empty fix is
 *  nothing to do. */
function applyFix(state: EditorState, dispatch: Dispatch, f: Fix, userEvent: string) {
  if (f.from === f.to && f.text === '') return;
  run(state, dispatch, {
    changes: { from: f.from, to: f.to, insert: f.text }, selection: EditorSelection.cursor(f.caret),
    annotations: exact(userEvent), scrollIntoView: true,
  });
}

/** A builder's edits as one exact transaction. */
type Sel = EditorSelection | { anchor: number; head?: number };

function commit(b: EditBuilder, dispatch: Dispatch, selection: Sel, userEvent = 'input.format') {
  if (b.empty) { dispatch(b.state.update({ selection, annotations: [ExactCaret.of(true)] })); return; }
  dispatch(b.state.update({ changes: b.changes, selection, annotations: exact(userEvent), scrollIntoView: true }));
}

const main = (state: EditorState) => state.selection.main;
/** A command that does nothing in a read-only editor (returns false, as CodeMirror's own do). */
export const editing = (command: StateCommand): StateCommand => (target) => !target.state.readOnly && command(target);
/** The body holding the whole selection, else −1 (`isWithinOneFragment`). */
const within = (state: EditorState) => layoutOf(state).within(main(state).from, main(state).to);
const screenplayAt = (state: EditorState, pos: number) => layoutOf(state).isScreenplay(layoutOf(state).bodyAt(pos));

// MARK: Emphasis

/** Italic (`*`) or bold (`**`) on or off for the selection, or for what's typed next when nothing is selected
 *  (`ScriveningsTextView.toggleMarker`). */
export function toggleMarker(markerText: string): StateCommand {
  return ({ state, dispatch }) => {
    const r = main(state);
    const i = within(state);
    // Wrapping a selection that crosses a divider would put it (and the next scene's opening) into one fragment.
    if (i < 0) return true;
    if (!layoutOf(state).isScreenplay(i)) {
      if (r.empty) {
        run(state, dispatch, { effects: setPendingEmphasis.of(toggledPending(state.field(pendingEmphasisField), markerText, r.head)) });
        return true;
      }
      const { w, base } = windowAround(state, i, r.from, r.to);
      const t = toggledEmphasis(w, r.from - base, r.to - base, markerText);
      if (!t) return true;
      run(state, dispatch, {
        changes: { from: t.from + base, to: t.to + base, insert: t.text },
        selection: EditorSelection.range(t.selection[0] + base, t.selection[1] + base),
        annotations: exact('input.format'), scrollIntoView: true,
      });
      return true;
    }
    // Fountain: wrapped in the markers, or unwrapped if already wrapped.
    const m = markerText.length;
    const doc = state.doc;
    if (r.from >= m && r.to + m <= doc.length && doc.sliceString(r.from - m, r.from) === markerText && doc.sliceString(r.to, r.to + m) === markerText) {
      const b = new EditBuilder(state);
      b.replace(r.to, r.to + m, '');
      b.replace(r.from - m, r.from, '');
      commit(b, dispatch, EditorSelection.single(r.from - m, r.to - m));
      return true;
    }
    // Nothing selected: what's typed next takes the style (no empty pair to leave behind).
    if (r.empty) {
      run(state, dispatch, { effects: setPendingEmphasis.of(toggledPending(state.field(pendingEmphasisField), markerText, r.head)) });
      return true;
    }
    const b = new EditBuilder(state);
    b.replace(r.from, r.to, markerText + doc.sliceString(r.from, r.to) + markerText);
    commit(b, dispatch, EditorSelection.single(r.from + m, r.to + m));
    return true;
  };
}

export const toggleItalic = editing(toggleMarker('*'));
export const toggleBold = editing(toggleMarker('**'));

/** Restyles every character the reader sees in the selection (or the caret's word), rebuilding the markup around it;
 *  `on` is false when all of them already have the style (`restyleSelection`). */
function restyleSelection(has: (s: InlineState) => boolean, apply: (s: InlineState, on: boolean) => void): StateCommand {
  return ({ state, dispatch }) => {
    const original = main(state);
    const i = within(state);
    if (i < 0 || layoutOf(state).isScreenplay(i)) return false;
    const { w, base } = windowAround(state, i, original.from, original.to);
    let a = original.from - base, b = original.to - base;
    if (a === b) {
      const found = word(w.t, a);
      if (!found) return true;
      [a, b] = found;
    }
    const fix = restyled(w, a, b, has, apply);
    if (!fix) return true;
    const selection = original.empty
      ? EditorSelection.cursor(base + Math.min(fix.selection[1], fix.selection[0] + (original.from - base) - a))
      : EditorSelection.range(fix.selection[0] + base, fix.selection[1] + base);
    run(state, dispatch, {
      changes: { from: fix.from + base, to: fix.to + base, insert: fix.text }, selection, annotations: exact('input.format'), scrollIntoView: true,
    });
    return true;
  };
}

export const toggleUnderline = editing(restyleSelection((s) => s.underline, (s, on) => { s.underline = on; }));
export const toggleSmallCaps = editing(restyleSelection((s) => s.smallCaps, (s, on) => { s.smallCaps = on; }));
/** Highlights the selection (or the caret's word) in `ink`; null takes highlights off. */
export const setHighlight = (ink: InkColor | null) => editing(restyleSelection((s) => s.highlight === ink, (s) => { s.highlight = ink; }));
/** Colours the selection's text (or the caret's word) `ink`; null takes colours off. */
export const setTextColor = (ink: InkColor | null) => editing(restyleSelection((s) => s.color === ink, (s) => { s.color = ink; }));

// MARK: Links

/** A link in the text: its words (document positions) and its address. */
export interface LinkAtSelection { from: number; to: number; address: string; fragmentId: string }

/** The link the selection is in (or the caret, in its words or at their end) (`linkAtSelection`). */
export function linkAtSelection(state: EditorState): LinkAtSelection | null {
  const r = main(state);
  const layout = layoutOf(state);
  const i = layout.bodyAt(r.from);
  if (i < 0 || layout.isScreenplay(i)) return null;
  const { w, base } = windowAround(state, i, r.from, r.from);
  const link = linkAt(w.t, r.from - base);
  if (!link || r.to > link.to + base) return null;
  return { from: link.from + base, to: link.to + base, address: link.address, fragmentId: layout.pieces[i].id };
}

/** Links the selection (or, with nothing selected, the link the caret is in, else the word at the caret) to the address
 *  the writer typed (`linkAddressFromTyped`) (`setLink`). False when that can't be an address, or the selection can't
 *  be linked (across scenes, in a screenplay). */
export function setLink(typed: string): StateCommand {
  return editing(({ state, dispatch }) => {
    const address = linkAddressFromTyped(typed);
    if (address === null) return false;
    const original = main(state);
    const i = within(state);
    if (i < 0 || layoutOf(state).isScreenplay(i)) return false;
    let from = original.from, to = original.to;
    if (from === to) {
      const link = linkAtSelection(state);
      if (link) [from, to] = [link.from, link.to];
      else {
        const { w, base } = windowAround(state, i, from, from);
        const found = word(w.t, from - base);
        if (!found) return false;
        [from, to] = [found[0] + base, found[1] + base];
      }
    }
    const { w, base } = windowAround(state, i, from, to);
    const fix = linked(w, from - base, to - base, address);
    if (!fix) return false;
    run(state, dispatch, {
      changes: { from: fix.from + base, to: fix.to + base, insert: fix.text },
      selection: EditorSelection.range(fix.selection[0] + base, fix.selection[1] + base),
      annotations: exact('input.format'), scrollIntoView: true,
    });
    return true;
  });
}

/** Takes the link off the selection, or with nothing selected the whole link the caret is in (`removeLink`). */
export const removeLink: StateCommand = editing(({ state, dispatch }) => {
  const original = main(state);
  const i = within(state);
  if (i < 0 || layoutOf(state).isScreenplay(i)) return false;
  const range = !original.empty ? [original.from, original.to] : (() => { const l = linkAtSelection(state); return l ? [l.from, l.to] : null; })();
  if (!range) return false;
  const { w, base } = windowAround(state, i, range[0], range[1]);
  const fix = linked(w, range[0] - base, range[1] - base, null);
  if (!fix) return false;
  const selection = !original.empty
    ? EditorSelection.range(fix.selection[0] + base, fix.selection[1] + base)
    : EditorSelection.cursor(fix.selection[1] + base);
  run(state, dispatch, {
    changes: { from: fix.from + base, to: fix.to + base, insert: fix.text }, selection, annotations: exact('input.format'), scrollIntoView: true,
  });
  return true;
});

// MARK: Blocks

interface Blocks { from: number; to: number; blocks: string[] }

/** The blocks the selection touches, from the first's start to the last's end (`selectedBlocks`). */
function selectedBlocks(state: EditorState): Blocks | null {
  const r = main(state);
  const i = within(state);
  if (i < 0 || layoutOf(state).isScreenplay(i)) return null;
  const bodyFrom = layoutOf(state).from[i];
  const text = state.doc.sliceString(bodyFrom, layoutOf(state).to[i]);
  let start = block(text, r.from - bodyFrom);
  let end = block(text, r.to - bodyFrom);
  // On a blank line: the block after it (or before, at the end).
  if (start[1] === start[0]) start = block(text, Math.min(text.length, start[0] + 1));
  if (end[1] === end[0] && !r.empty) end = block(text, Math.max(0, end[0] - 1));
  if (start[1] === start[0] && end[1] === end[0]) return null;
  const lo = Math.min(start[0], end[0]), hi = Math.max(start[1], end[1]);
  const blocks = text.slice(lo, hi).split('\n\n').filter((b) => b.trim().length > 0);
  return { from: bodyFrom + lo, to: bodyFrom + hi, blocks };
}

/** Replaces the selected blocks, keeping the caret where it was in the text (or the blocks selected). */
function replaceBlocks(state: EditorState, dispatch: Dispatch, sel: Blocks, next: string[]) {
  const text = next.join('\n\n');
  if (text === sel.blocks.join('\n\n')) return;
  const caret = main(state);
  let selection: Sel;
  if (caret.empty && sel.blocks.length === 1 && next.length === 1) {
    // One block: only its first line's markup changed length (a marker, a fence) before the caret.
    const shift = leadLength(next[0]) - leadLength(sel.blocks[0]);
    selection = EditorSelection.cursor(sel.from + Math.max(0, Math.min(text.length, caret.from - sel.from + shift)));
  } else {
    selection = EditorSelection.range(sel.from, sel.from + text.length);
  }
  run(state, dispatch, { changes: { from: sel.from, to: sel.to, insert: text }, selection, annotations: exact('input.format'), scrollIntoView: true });
}

/** Sets the selected paragraphs flush left (null), centred, right or justified. */
export function setAlignment(a: Alignment | null): StateCommand {
  return editing(({ state, dispatch }) => {
    const sel = selectedBlocks(state);
    if (!sel) return false;
    replaceBlocks(state, dispatch, sel, aligned(sel.blocks, a));
    return true;
  });
}

/** Makes the selected blocks a list of `style`, or (null, or all of them already that list) paragraphs again. */
export function setList(style: ListStyle | null): StateCommand {
  return editing(({ state, dispatch }) => {
    const sel = selectedBlocks(state);
    if (!sel) return false;
    const all = sel.blocks.filter((b) => !isSceneBreakOrNote(b)).every((b) => listStyleOf(b) === style);
    replaceBlocks(state, dispatch, sel, listed(sel.blocks, all ? null : style));
    return true;
  });
}

/** The caret's block in its body (document offsets). */
function caretBlock(state: EditorState): { from: number; to: number; bodyFrom: number; bodyTo: number } | null {
  const layout = layoutOf(state);
  const caret = main(state).head;
  const i = layout.bodyAt(caret);
  if (i < 0) return null;
  const { w, base } = windowAround(state, i, caret, caret);
  const [a, b] = block(w.t, caret - base);
  return { from: a + base, to: b + base, bodyFrom: w.bodyFrom + base, bodyTo: w.bodyTo + base };
}

/** Makes the caret's block body text, a block quote, an epigraph or verse. A selection across several blocks restyles
 *  each (a stanza each, for verse), or makes them all body text when every one is already that style. */
export function setBlockStyle(style: BlockStyle): StateCommand {
  return editing(({ state, dispatch }) => {
    if (screenplayAt(state, main(state).head)) return false;
    if (!main(state).empty) {
      const sel = selectedBlocks(state);
      if (sel && sel.blocks.length > 1) {
        const prose = sel.blocks.filter((x) => !isSceneBreakOrNote(x));
        const target: BlockStyle = prose.every((x) => blockStyleOf(x) === style) ? 'body' : style;
        replaceBlocks(state, dispatch, sel, sel.blocks.map((x) => (isSceneBreakOrNote(x) ? x : restyle(x, target))));
        return true;
      }
    }
    const b = caretBlock(state);
    if (!b) return true;
    if (b.to === b.from) {
      if (style === 'verse' && main(state).empty) openStanza(state, dispatch, b.from);
      return true;
    }
    const old = state.doc.sliceString(b.from, b.to);
    let next = restyle(old, style);
    if (next === old) return true;
    // A fence the editor writes keeps a blank line either side, so the blocks round it stay their own.
    const at = (i: number) => state.doc.sliceString(i, i + 1);
    if (next.startsWith(':::') && b.from - 2 >= b.bodyFrom && at(b.from - 1) === '\n' && at(b.from - 2) !== '\n') next = '\n' + next;
    if (next.endsWith(':::') && b.to + 2 <= b.bodyTo && at(b.to) === '\n' && at(b.to + 1) !== '\n') next += '\n';
    const end = b.from + next.length - (next.endsWith('\n') ? 1 : 0);
    run(state, dispatch, {
      changes: { from: b.from, to: b.to, insert: next }, selection: EditorSelection.cursor(end),
      annotations: exact('input.format'), scrollIntoView: true,
    });
    return true;
  });
}

/** Verse chosen on an empty line: an empty stanza there to type into (`::: verse`, an empty line, `:::`), with a blank
 *  line either side as every fence the editor writes has (counting the newlines already there; none before it at the
 *  body's start or after it at the end), the caret on its line. Nothing on a line with spaces or inside another fence
 *  (`openStanza`, Swift). */
function openStanza(state: EditorState, dispatch: Dispatch, caret: number): void {
  const layout = layoutOf(state);
  const i = layout.bodyAt(caret);
  if (i < 0) return;
  const start = layout.from[i], end = layout.to[i];
  const at = (i: number) => state.doc.sliceString(i, i + 1);
  if (!(caret === start || at(caret - 1) === '\n') || !(caret === end || at(caret) === '\n')) return;
  if (isInsideFence(state.doc.sliceString(start, end), caret - start)) return;
  let before = 0, after = 0;
  while (before < 2 && caret - before > start && at(caret - before - 1) === '\n') before++;
  while (after < 2 && caret + after < end && at(caret + after) === '\n') after++;
  const lead = (caret === start ? '' : '\n'.repeat(2 - before)) + verseFence(null) + '\n';
  const trail = caret === end ? '' : '\n'.repeat(2 - after);
  run(state, dispatch, {
    changes: { from: caret, insert: lead + '\n:::' + trail }, selection: EditorSelection.cursor(caret + lead.length),
    annotations: exact('input.format'), scrollIntoView: true,
  });
}

/** A scene break where the caret is, as its own block: one blank line either side, counting the newlines already
 *  there (more would leave empty paragraphs), none before it at the body's start; the caret goes to the start of what
 *  follows (`insertSceneBreak`, Swift). */
export const insertSceneBreak: StateCommand = editing(({ state, dispatch }) => {
  const r = main(state);
  const layout = layoutOf(state);
  const i = layout.within(r.from, r.to);
  if (i < 0) return true;
  const start = layout.from[i], end = layout.to[i];
  let before = 0, after = 0;
  while (before < 2 && r.from - before > start && state.doc.sliceString(r.from - before - 1, r.from - before) === '\n') before++;
  while (after < 2 && r.to + after < end && state.doc.sliceString(r.to + after, r.to + after + 1) === '\n') after++;
  const lead = r.from === start ? '' : '\n'.repeat(2 - before);
  const insert = lead + '***' + '\n'.repeat(2 - after);
  run(state, dispatch, {
    changes: { from: r.from, to: r.to, insert }, selection: EditorSelection.cursor(r.from + lead.length + 5),
    annotations: exact('input.format'), scrollIntoView: true,
  });
  return true;
});

/** A numbered footnote: the reference at the caret, its note at the end of the scene, and the caret there ready to
 *  write it — or, with `text` (a citation), the note filled and the caret left after the reference. */
export function insertFootnote(text?: string): StateCommand {
  return editing(({ state, dispatch }) => {
    const r = main(state);
    const layout = layoutOf(state);
    const i = layout.bodyAt(r.from);
    if (i < 0) return false;
    const number = nextFootnoteNumber(state.doc.sliceString(layout.from[i], layout.to[i]));
    const reference = `[^${number}]`;
    const b = new EditBuilder(state);
    b.replace(r.to, r.to, reference);
    const grownEnd = layout.to[i] + (r.to <= layout.to[i] ? reference.length : 0);
    const note = `\n\n[^${number}]: ` + (text ?? '');
    b.replace(grownEnd, grownEnd, note);
    commit(b, dispatch, EditorSelection.cursor(text === undefined ? grownEnd + note.length : r.to + reference.length));
    return true;
  });
}

// MARK: Lists

interface ListItem { bodyFrom: number; bodyTo: number; block: [number, number]; lineStart: number; contentStart: number; lineEnd: number; style: ListStyle; number: number }

/** A list item's line around `location` (`listItem(at:)`), in `doc` (a builder's text), within body [bodyFrom, bodyTo). */
function listItemAt(doc: { sliceString(a: number, b: number): string }, location: number, bodyFrom: number, bodyTo: number): ListItem | null {
  const local = doc.sliceString(bodyFrom, bodyTo);
  const bl = block(local, location - bodyFrom);
  if (bl[1] === bl[0]) return null;
  const blockRange: [number, number] = [bodyFrom + bl[0], bodyFrom + bl[1]];
  const source = local.slice(bl[0], bl[1]);
  const blocks = parseProse(source);
  const markers = scan(source).filter((s) => s.kind === 'listMarker');
  const offset = location - blockRange[0];
  let index = -1;
  for (let k = 0; k < markers.length; k++) if (markers[k].from <= offset) index = k;
  if (index < 0 || blocks.length !== markers.length) return null;
  const kind = blocks[index].kind;
  if (kind.type !== 'listItem') return null;
  const m = markers[index];
  const lineStart = blockRange[0] + m.from;
  let lineEnd = source.indexOf('\n', m.from);
  lineEnd = lineEnd < 0 ? blockRange[1] : blockRange[0] + lineEnd;
  // The caret must be on the marker's own line.
  if (location > lineEnd) return null;
  return { bodyFrom, bodyTo, block: blockRange, lineStart, contentStart: lineStart + (m.to - m.from), lineEnd, style: kind.style, number: kind.number };
}

/** Rewrites the numbers of the list around `location` that no longer count on from its first item, each marker alone
 *  (`renumberList`). Returns how far the caret moved. */
function renumber(b: EditBuilder, location: number, bodyFrom: number, bodyTo: number, caret: number): number {
  const local = b.slice(bodyFrom, bodyTo);
  const bl = block(local, Math.min(local.length, Math.max(0, location - bodyFrom)));
  if (bl[1] === bl[0]) return 0;
  const source = local.slice(bl[0], bl[1]);
  const items = parseProse(source);
  const markers = scan(source).filter((s) => s.kind === 'listMarker');
  if (items.length !== markers.length) return 0;
  let shift = 0;
  for (let k = items.length - 1; k >= 0; k--) {
    const kind = items[k].kind;
    if (kind.type !== 'listItem') continue;
    const want = listMarkerText(kind.style, kind.number) + '\t';
    const from = bodyFrom + bl[0] + markers[k].from, to = bodyFrom + bl[0] + markers[k].to;
    if (b.slice(from, to) === want) continue;
    b.replace(from, to, want);
    if (from < caret) shift += want.length - (to - from);
  }
  return shift;
}

/** Takes an item out of its list: its marker goes and blank lines part it from the items around it; the items after it
 *  carry on the numbering from the ones before (`endItem`). Returns the caret. */
function endItem(b: EditBuilder, item: ListItem): number {
  const markerLength = item.contentStart - item.lineStart;
  const hasNext = item.lineEnd < item.bodyTo && b.slice(item.lineEnd, item.lineEnd + 1) === '\n';
  const isFirst = item.lineStart === item.block[0];
  // After the line first, so the earlier ranges hold.
  if (hasNext) b.replace(item.lineEnd, item.lineEnd + 1, '\n\n');
  let caret: number;
  if (isFirst) {
    b.replace(item.lineStart, item.lineStart + markerLength, '');
    caret = item.lineStart;
  } else {
    // The newline before the item becomes a paragraph break.
    b.replace(item.lineStart - 1, item.lineStart + markerLength, '\n\n');
    caret = item.lineStart + 1;
  }
  if (hasNext && isNumbered(item.style)) {
    const shift = isFirst ? -markerLength : 1 - markerLength;
    const next = item.lineEnd + 2 + shift, bodyEnd = item.bodyTo + 1 + shift;
    const line = b.slice(next, next + Math.min(16, bodyEnd - next));
    const m = parseListMarker(line);
    if (m) {
      const want = listMarkerText(item.style, item.number) + '\t';
      b.replace(next, next + m.length, want);
      renumber(b, next, item.bodyFrom, bodyEnd + want.length - m.length, caret);
    }
  }
  return caret;
}

/** Return in a list item: the next item (the item's text after the caret goes with it), numbered on, with the items
 *  after it renumbered; on an empty item the list ends there instead (`continueList`). False outside a list. */
export const continueList: StateCommand = editing(({ state, dispatch }) => {
  const r = main(state);
  const layout = layoutOf(state);
  const i = within(state);
  if (i < 0 || layout.isScreenplay(i)) return false;
  const item = listItemAt(state.doc, r.from, layout.from[i], layout.to[i]);
  if (!item || r.from < item.contentStart) return false;
  const b = new EditBuilder(state);
  const content = state.doc.sliceString(item.contentStart, item.lineEnd);
  if (trimmed(content).length === 0 && r.empty) {
    const caret = endItem(b, item);
    commit(b, dispatch, EditorSelection.cursor(caret), 'input');
    return true;
  }
  const markerText = listMarkerText(item.style, item.number + 1) + '\t';
  const { w, base } = windowAround(state, i, r.from, r.to);
  const fix = inserting(w, [{ kind: 'newline', text: '\n' }, { kind: 'syntax', text: markerText }], r.from - base, r.to - base);
  b.replace(fix.from + base, fix.to + base, fix.text);
  const caret = fix.caret + base;
  const shift = renumber(b, caret, layout.from[i], b.changes.mapPos(layout.to[i], 1), caret);
  commit(b, dispatch, EditorSelection.cursor(caret + shift), 'input');
  return true;
});

/** Backspace just after an item's marker: the marker goes and the item becomes a paragraph of its own (the list splits
 *  around it) (`removeListMarker`). False when the caret isn't there. */
export const removeListMarker: StateCommand = editing(({ state, dispatch }) => {
  const r = main(state);
  const layout = layoutOf(state);
  if (!r.empty) return false;
  const i = layout.bodyAt(r.from);
  if (i < 0 || layout.isScreenplay(i)) return false;
  const item = listItemAt(state.doc, r.from, layout.from[i], layout.to[i]);
  if (!item || r.from !== item.contentStart) return false;
  const b = new EditBuilder(state);
  const caret = endItem(b, item);
  commit(b, dispatch, EditorSelection.cursor(caret), 'delete.backward');
  return true;
});

/** Renumbers the list around the caret. */
export const renumberList: StateCommand = editing(({ state, dispatch }) => {
  const r = main(state);
  const layout = layoutOf(state);
  const i = layout.bodyAt(r.from);
  if (i < 0) return false;
  const b = new EditBuilder(state);
  const shift = renumber(b, r.from, layout.from[i], layout.to[i], r.from);
  if (!b.empty) commit(b, dispatch, EditorSelection.range(r.anchor + (r.anchor >= r.from ? shift : 0), r.head + shift));
  return true;
});

// MARK: Return, Backspace, Delete

/** A paragraph or hard break, with emphasis open at the caret closed before it and opened again after (`insertBreak`). */
function insertBreak(text: string): StateCommand {
  return ({ state, dispatch }) => {
    const r = main(state);
    const layout = layoutOf(state);
    const i = within(state);
    const cleared = state.field(pendingEmphasisField) ? [setPendingEmphasis.of(null)] : [];
    if (i >= 0 && !layout.isScreenplay(i)) {
      const { w, base } = windowAround(state, i, r.from, r.to);
      const fix = lineBreak(w, r.from - base, r.to - base, text);
      if (fix) {
        run(state, dispatch, {
          changes: { from: fix.from + base, to: fix.to + base, insert: fix.text }, selection: EditorSelection.cursor(fix.caret + base),
          effects: cleared, annotations: exact('input'), scrollIntoView: true,
        });
        return true;
      }
    }
    // As typed: the filter decides (a selection across fragments splits, the text going into the first).
    run(state, dispatch, {
      changes: { from: r.from, to: r.to, insert: text }, selection: EditorSelection.cursor(r.from + text.length),
      effects: cleared, userEvent: 'input', scrollIntoView: true,
    });
    return true;
  };
}

/** Return: in a screenplay the next element; in verse the next line; in a list the next item; else a new paragraph
 *  (`insertNewline`). */
export const insertNewline: StateCommand = editing((target) => {
  const { state } = target;
  if (screenplayAt(state, main(state).from)) return screenplayNewline(false)(target);
  if (continueVerse(target)) return true;
  if (continueList(target)) return true;
  return insertBreak('\n\n')(target);
});

/** Shift-Return: a hard line break (`insertLineBreak`); in a screenplay a single newline; in verse as Return. */
export const insertLineBreak: StateCommand = editing((target) => {
  if (screenplayAt(target.state, main(target.state).from)) return screenplayNewline(true)(target);
  if (continueVerse(target)) return true;
  return insertBreak('\\\n')(target);
});

// MARK: Verse

/** Return in a verse block (`continueVerse`): a new line of the stanza at the same step (the text after the caret goes
 *  with it). On an empty last line it ends the stanza instead (a new fence opens for the next); on a stanza with no
 *  other line it ends the verse block, leaving an empty paragraph. At the end of a fence's opening line typed by hand,
 *  with nothing after it yet, it starts the first line. False when the caret isn't on a line of verse. */
export const continueVerse: StateCommand = editing(({ state, dispatch }) => {
  const r = main(state);
  const i = within(state);
  if (i < 0 || layoutOf(state).isScreenplay(i)) return false;
  const { w, base } = bodyWindow(state, i);
  const opened = r.empty ? verseOpenerEnd(w, r.from - base) : null;
  if (opened !== null) {
    run(state, dispatch, {
      changes: { from: opened + base, insert: '\n' }, selection: EditorSelection.cursor(opened + base + 1),
      annotations: exact('input'), scrollIntoView: true,
    });
    return true;
  }
  const verse = verseLine(w, r.from - base);
  if (!verse) return false;
  if (r.empty && verse.isEmpty && verse.line[0] === verse.lines[verse.lines.length - 1][0]) {
    if (verse.lines.length === 1) {
      run(state, dispatch, {
        changes: { from: verse.block[0] + base, to: verse.block[1] + base, insert: '' }, selection: EditorSelection.cursor(verse.block[0] + base),
        annotations: exact('input'), scrollIntoView: true,
      });
    } else {
      // The hard break before this line goes with it; the next stanza's fence opens after the close.
      let from = verse.line[0] - 1;
      if (from - 1 >= verse.block[0] && w.t.charCodeAt(from - 1) === 92) from--;
      const replacement = '\n:::\n\n' + verseFence(verse.alignment) + '\n';
      run(state, dispatch, {
        changes: { from: from + base, to: verse.line[1] + base, insert: replacement }, selection: EditorSelection.cursor(from + replacement.length + base),
        annotations: exact('input'), scrollIntoView: true,
      });
    }
    return true;
  }
  const tabs = '\t'.repeat(verse.tabs);
  const fix = inserting(w, [{ kind: 'newline', text: '\\\n' }, { kind: 'syntax', text: tabs }], r.from - base, r.to - base);
  run(state, dispatch, {
    changes: { from: fix.from + base, to: fix.to + base, insert: fix.text }, selection: EditorSelection.cursor(fix.caret + base),
    annotations: exact('input'), scrollIntoView: true,
  });
  return true;
});

/** Tab (or Shift-Tab, `backwards`) in a verse block (`verseTab`): one step in (or out) on the caret's line, or on every
 *  line of the stanza the selection touches. False when the caret isn't on a line of verse. */
export function verseTab(backwards: boolean): StateCommand {
  return editing(({ state, dispatch }) => {
    const r = main(state);
    const i = within(state);
    if (i < 0 || layoutOf(state).isScreenplay(i)) return false;
    const { w, base } = bodyWindow(state, i);
    const verse = verseLine(w, r.from - base);
    if (!verse) return false;
    const touched = verse.lines.filter(([a, b]) => a <= r.to - base && b >= r.from - base);
    if (touched.length === 0) return true;
    const b = new EditBuilder(state);
    let start = r.from, end = r.to;
    // Last line first, so the earlier ranges hold.
    for (const [lineFrom] of [...touched].reverse()) {
      const at = lineFrom + base;
      let tabs = 0;
      while (w.t.charCodeAt(lineFrom + tabs) === 9) tabs++;
      let delta: number;
      if (backwards) {
        if (tabs === 0) continue;
        b.replace(at, at + 1, '');
        delta = -1;
      } else {
        if (tabs >= MAX_VERSE_INDENT) continue;
        b.replace(at, at, '\t');
        delta = 1;
      }
      if (at <= start) start = Math.max(at, start + delta);
      if (at <= end) end = Math.max(at, end + delta);
    }
    if (b.empty) return true;
    commit(b, dispatch, EditorSelection.range(start, Math.max(start, end)), 'input.format');
    return true;
  });
}

/** The transaction Backspace or Delete makes (`deleteBackward`/`deleteForward`): an item's marker taken off, a
 *  paragraph joined (both newlines), hidden syntax kept balanced; in a screenplay one character. Null when there's
 *  nothing to delete. */
export function deleteSpec(state: EditorState, backward: boolean): TransactionSpec | null {
  const range = deleteRange(state, backward);
  if (!range) return null;
  return {
    changes: { from: range[0], to: range[1] }, selection: EditorSelection.cursor(range[0]),
    userEvent: backward ? 'delete.backward' : 'delete.forward', scrollIntoView: true,
  };
}

/** The range Backspace or Delete removes before balancing; null when there's nothing to delete. */
export function deleteRange(state: EditorState, backward: boolean): [number, number] | null {
  const r = main(state);
  const layout = layoutOf(state);
  if (layout.count === 0) return null;
  const i = layout.bodyAt(r.head);
  const screenplay = layout.isScreenplay(i);
  let range: [number, number] | null;
  if (!r.empty) range = [r.from, r.to];
  else if (i < 0) return null;
  else if (screenplay) {
    // One character, never a paragraph join or balancing: a script's newlines each mean something.
    if (backward && r.head > layout.from[i]) {
      const lo = Math.max(layout.from[i], r.head - 16);
      range = [lo + clusterBefore(state.doc.sliceString(lo, r.head), r.head - lo)[0], r.head];
    } else if (!backward && r.head < layout.to[i]) {
      const hi = Math.min(layout.to[i], r.head + 16);
      range = [r.head, r.head + clusterAfter(state.doc.sliceString(r.head, hi), 0)[1]];
    } else range = null;
  } else {
    const { w, base } = windowAround(state, i, r.head, r.head);
    const k = keyDeleteRange(w, r.head - base, r.head - base, backward);
    range = k ? [k[0] + base, k[1] + base] : null;
  }
  // At a fragment's edge: nothing (the divider is never deleted).
  if (!range || range[0] === range[1]) return null;
  return range;
}

/** Backspace. */
export const deleteBackward: StateCommand = editing((target) => {
  if (removeListMarker(target)) return true;
  const spec = deleteSpec(target.state, true);
  if (spec) run(target.state, target.dispatch, spec);
  return true;
});

/** Delete (forward). */
export const deleteForward: StateCommand = editing((target) => {
  const spec = deleteSpec(target.state, false);
  if (spec) run(target.state, target.dispatch, spec);
  return true;
});

/** A deletion ending (or starting) at the caret that came from the input system rather than a key (an Android
 *  keyboard, `execCommand('delete')`, a spelling panel), so it can be made as Backspace (or Delete) would make it:
 *  'backward', 'forward', or null when it isn't one. */
export function inputDeletion(state: EditorState, a: number, b: number): 'backward' | 'forward' | null {
  const r = main(state);
  if (!r.empty || b <= a || b - a > 4) return null;
  return b === r.head ? 'backward' : a === r.head ? 'forward' : null;
}

/** What a command would dispatch, as a spec (for the filter, which can't dispatch). */
export function captured(command: StateCommand, state: EditorState): TransactionSpec | null {
  let tr: Transaction | null = null;
  command({ state, dispatch: (t) => { tr = t; } });
  const t = tr as Transaction | null;
  if (!t) return null;
  const event = t.annotation(Transaction.userEvent);
  return {
    changes: t.changes, selection: t.selection, effects: t.effects, scrollIntoView: t.scrollIntoView,
    annotations: [Exact.of(t.annotation(Exact) === true), ExactCaret.of(t.annotation(ExactCaret) === true), ...(event ? [Transaction.userEvent.of(event)] : [])],
  };
}

/** Tab: in a screenplay the next element (Shift-Tab the one before); in prose a tab character, as typed (a list
 *  marker is a marker and a tab). */
export function insertTab(backwards: boolean): StateCommand {
  return editing((target) => {
    const { state } = target;
    if (screenplayAt(state, main(state).head)) return screenplayTab(backwards)(target);
    if (verseTab(backwards)(target)) return true;
    if (backwards) return false;
    const r = main(state);
    run(state, target.dispatch, { changes: { from: r.from, to: r.to, insert: '\t' }, selection: EditorSelection.cursor(r.from + 1), userEvent: 'input.type', scrollIntoView: true });
    return true;
  });
}

// MARK: The format bar's state

/** The formatting where the caret is (`ProseFormat`): the block's style, alignment and list, and the inline styles at the
 *  selection's start. */
export interface ProseFormat {
  style: BlockStyle;
  alignment: Alignment | null;
  list: ListStyle | null;
  inline: InlineState;
  /** The link the selection is in, if any (`linkAtSelection`): its address. */
  link: string | null;
}

export function proseFormat(state: EditorState): ProseFormat {
  const r = main(state);
  const b = caretBlock(state);
  const inline = { italic: false, bold: false, smallCaps: false, underline: false, highlight: null, color: null, link: null };
  if (!b) return { style: 'body', alignment: null, list: null, inline, link: null };
  const source = state.doc.sliceString(b.from, b.to);
  const at = Math.min(r.empty ? r.from : r.from + 1, b.to);
  const i = layoutOf(state).bodyAt(at);
  const { w, base } = windowAround(state, i, at, at);
  const link = linkAtSelection(state)?.address ?? null;
  // An empty line of a stanza (a new stanza's, waiting for its words) is verse.
  if (b.to === b.from) {
    const whole = bodyWindow(state, i);
    const verse = verseLine(whole.w, r.from - whole.base);
    if (verse) return { style: 'verse', alignment: verse.alignment, list: null, inline: stateAt(w.t, at - base), link };
  }
  return {
    style: blockStyleOf(source), alignment: alignmentOf(source), list: listStyleOf(source),
    inline: stateAt(w.t, at - base), link,
  };
}

export { setScreenplayElement, screenplayTab };
